import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  MoreVertical,
  Search,
  Shield,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { teamService, userService } from "@/lib/services";
import type { Team, User } from "@/types";

function getStatusStyle(isActive: boolean, lastLoginAt: string | null | undefined): string {
  if (!isActive) return "bg-surface-tertiary/20 text-content-tertiary";
  if (!lastLoginAt) return "bg-amber-500/20 text-amber-400";
  return "bg-green-500/20 text-green-400";
}

function getStatusContent(isActive: boolean, lastLoginAt: string | null | undefined) {
  if (!isActive) {
    return (
      <>
        <XCircle className="h-3 w-3" />
        Inactive
      </>
    );
  }
  if (!lastLoginAt) {
    return (
      <>
        <Clock className="h-3 w-3" />
        Pending
      </>
    );
  }
  return (
    <>
      <CheckCircle2 className="h-3 w-3" />
      Active
    </>
  );
}

interface UserActionMenuProps {
  user: User;
  isActive: boolean;
  isOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  onToggle: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  onDelete: () => void;
}

function UserActionMenu({
  user,
  isActive,
  isOpen,
  menuRef,
  onToggle,
  onDeactivate,
  onActivate,
  onDelete,
}: Readonly<UserActionMenuProps>) {
  return (
    <div className="flex items-center justify-end gap-1 relative">
      {(user.role === "platform_admin" || user.role === "install_admin") && (
        <Shield className="h-4 w-4 text-purple-400" />
      )}
      <button
        onClick={onToggle}
        className="p-1 rounded-md hover:bg-interactive-active text-content-tertiary hover:text-content-primary transition-colors"
        title="User actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 top-8 z-10 w-48 bg-surface-primary border border-border-secondary rounded-lg shadow-lg py-1"
        >
          {isActive ? (
            <button
              onClick={onDeactivate}
              className="w-full px-3 py-2 text-left text-sm text-content-primary hover:bg-interactive-hover flex items-center gap-2"
            >
              <UserX className="h-4 w-4 text-red-400" />
              Deactivate User
            </button>
          ) : (
            <button
              onClick={onActivate}
              className="w-full px-3 py-2 text-left text-sm text-content-primary hover:bg-interactive-hover flex items-center gap-2"
            >
              <UserCheck className="h-4 w-4 text-green-400" />
              Activate User
            </button>
          )}
          {!isActive && !user.lastLoginAt && (
            <button
              onClick={onDelete}
              className="w-full px-3 py-2 text-left text-sm text-content-primary hover:bg-interactive-hover flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4 text-red-400" />
              Delete User
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function AdminUsers() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { user: currentUser } = useAuth();
  const isInstallAdmin = currentUser?.role === "install_admin";
  const [searchQuery, setSearchQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionMenuUser, setActionMenuUser] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    user: User;
    action: "activate" | "deactivate";
  } | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: "", name: "", role: "member" });
  const [addUserTeams, setAddUserTeams] = useState<string[]>([]);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const pageSize = 20;

  useEffect(() => {
    loadUsers();
  }, [page, searchQuery, includeInactive]);

  // Load teams when the add-user modal opens
  useEffect(() => {
    if (showAddUser && availableTeams.length === 0) {
      teamService.list().then((res) => {
        const teams = (res.data || []).filter((t) => !t.isEveryoneTeam);
        setAvailableTeams(teams);
      });
    }
  }, [showAddUser]);

  // Close action menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActionMenuUser(null);
      }
    };
    if (actionMenuUser) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [actionMenuUser]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.list({
        page,
        pageSize,
        search: searchQuery || undefined,
        includeInactive,
      });
      const data = response.data || [];
      // Deleting or deactivating the only user on the last page leaves it empty
      if (data.length === 0 && page > 1) {
        setPage(page - 1);
        return;
      }
      setUsers(data);
      setTotal(response.meta?.total || 0);
    } catch (err) {
      console.error("Failed to load users:", err);
      showToast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (user: User) => {
    try {
      await userService.update(user.uuid, { is_active: true });
      showToast(`${user.name || user.email} has been activated`, "success");
      setConfirmDialog(null);
      await loadUsers();
    } catch (err) {
      console.error("Failed to activate user:", err);
      if (err instanceof ApiError && err.data) {
        const data = err.data as { detail?: { code?: string; message?: string } | string };
        const detail = data.detail;
        if (typeof detail === "object" && detail?.code === "SEAT_LIMIT_REACHED") {
          showToast(detail.message || "Seat limit reached — cannot activate user", "error");
        } else {
          showToast(typeof detail === "string" ? detail : "Failed to activate user", "error");
        }
      } else {
        showToast("Failed to activate user", "error");
      }
      setConfirmDialog(null);
    }
  };

  const handleDeactivate = async (user: User) => {
    try {
      await userService.update(user.uuid, {
        is_active: false,
        deactivation_reason: "Account deactivated by administrator.",
      });
      showToast(`${user.name || user.email} has been deactivated`, "success");
      setConfirmDialog(null);
      await loadUsers();
    } catch (err) {
      console.error("Failed to deactivate user:", err);
      showToast("Failed to deactivate user", "error");
    }
  };

  const handleDelete = async (user: User) => {
    const confirmed = await confirm({
      title: "Delete User",
      message: `Permanently delete ${user.email}? Their team and project memberships and anything shared with them will be removed too. If someone signs in with this email later, they'll start as a new user.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await userService.delete(user.uuid);
      showToast(`${user.email} has been deleted`, "success");
      await loadUsers();
    } catch (err) {
      console.error("Failed to delete user:", err);
      showToast(getApiErrorMessage(err, "Failed to delete user"), "error");
    }
  };

  const handleChangeRole = async (user: User, newRole: string) => {
    try {
      await userService.update(user.uuid, { role: newRole });
      showToast("User role updated successfully", "success");
      await loadUsers();
    } catch (err) {
      console.error("Failed to update user role:", err);
      showToast("Failed to update user role", "error");
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addUserForm.email || !addUserForm.name) return;

    try {
      setAddUserSubmitting(true);
      await userService.create({
        email: addUserForm.email,
        name: addUserForm.name,
        role: addUserForm.role,
        team_uuids: addUserTeams,
      });
      showToast(`${addUserForm.name} has been added`, "success");
      setShowAddUser(false);
      setAddUserForm({ email: "", name: "", role: "member" });
      setAddUserTeams([]);
      await loadUsers();
    } catch (err) {
      console.error("Failed to add user:", err);
      if (err instanceof ApiError && err.data) {
        const data = err.data as { detail?: string };
        showToast(data.detail || "Failed to add user", "error");
      } else {
        showToast("Failed to add user", "error");
      }
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const handleTeamCheckboxChange = (teamUuid: string, checked: boolean) => {
    setAddUserTeams((t) => (checked ? [...t, teamUuid] : t.filter((id) => id !== teamUuid)));
  };

  const formatLastLogin = (user: User) => {
    const dateString = user.lastLoginAt;
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <select
            aria-label="Filter users by status"
            value={includeInactive ? "all" : "active"}
            onChange={(e) => {
              setIncludeInactive(e.target.value === "all");
              setPage(1);
            }}
            className="px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
          >
            <option value="active">Active users</option>
            <option value="all">All users</option>
          </select>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-content-tertiary">
            {total} user{total === 1 ? "" : "s"} total
          </span>
          <button
            onClick={() => setShowAddUser(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary/90 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Add User
          </button>
        </div>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-primary">
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {users.map((user) => {
              const isActive = user.isActive ?? true;
              return (
                <tr key={user.uuid} className={`hover:bg-interactive-hover ${!isActive ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white ${
                          isActive ? "bg-brand-gradient-br" : "bg-surface-tertiary"
                        }`}
                      >
                        {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-content-primary">{user.name || "Unknown"}</div>
                        <div className="text-xs text-content-tertiary">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {user.role === "install_admin" && !isInstallAdmin ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        Install Admin
                      </span>
                    ) : (
                      <select
                        value={user.role}
                        onChange={(e) => handleChangeRole(user, e.target.value)}
                        disabled={!isActive}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-secondary border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.role === "platform_admin" || user.role === "install_admin"
                            ? "text-purple-400"
                            : "text-content-tertiary"
                        }`}
                      >
                        <option value="member">Member</option>
                        <option value="platform_admin">Platform Admin</option>
                        {isInstallAdmin && <option value="install_admin">Install Admin</option>}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium w-fit ${getStatusStyle(
                          isActive,
                          user.lastLoginAt,
                        )}`}
                      >
                        {getStatusContent(isActive, user.lastLoginAt)}
                      </span>
                      {!isActive && user.deactivationReason && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400/80 max-w-[200px]">
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          <span className="truncate" title={user.deactivationReason}>
                            {user.deactivationReason}
                          </span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-content-tertiary">{formatLastLogin(user)}</td>
                  <td className="px-4 py-3 text-right">
                    <UserActionMenu
                      user={user}
                      isActive={isActive}
                      isOpen={actionMenuUser === user.uuid}
                      menuRef={menuRef}
                      onToggle={() => setActionMenuUser(actionMenuUser === user.uuid ? null : user.uuid)}
                      onDeactivate={() => {
                        setActionMenuUser(null);
                        setConfirmDialog({ user, action: "deactivate" });
                      }}
                      onActivate={() => {
                        setActionMenuUser(null);
                        setConfirmDialog({ user, action: "activate" });
                      }}
                      onDelete={() => {
                        setActionMenuUser(null);
                        void handleDelete(user);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {users.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-content-quaternary mx-auto mb-3" />
          <p className="text-content-tertiary">{searchQuery ? "No users found" : "No users yet"}</p>
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-surface-secondary text-content-primary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-interactive-active transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-content-tertiary">
            Page {page} of {Math.ceil(total / pageSize)}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(total / pageSize)}
            className="px-3 py-1 bg-surface-secondary text-content-primary rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-interactive-active transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-content-primary mb-2">
              {confirmDialog.action === "deactivate" ? "Deactivate User" : "Activate User"}
            </h3>
            <p className="text-sm text-content-secondary mb-4">
              {confirmDialog.action === "deactivate" ? (
                <>
                  Are you sure you want to deactivate{" "}
                  <span className="font-medium text-content-primary">
                    {confirmDialog.user.name || confirmDialog.user.email}
                  </span>
                  ? They will be unable to access the platform and their seat will be freed up for other users.
                </>
              ) : (
                <>
                  Are you sure you want to activate{" "}
                  <span className="font-medium text-content-primary">
                    {confirmDialog.user.name || confirmDialog.user.email}
                  </span>
                  ? This will count as a billable seat. Make sure you have available seats in your license.
                </>
              )}
            </p>
            {confirmDialog.action === "activate" && confirmDialog.user.deactivationReason && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-xs text-amber-400 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    <span className="font-medium">Previous deactivation reason:</span>{" "}
                    {confirmDialog.user.deactivationReason}
                  </span>
                </p>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmDialog.action === "deactivate") {
                    handleDeactivate(confirmDialog.user);
                  } else {
                    handleActivate(confirmDialog.user);
                  }
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  confirmDialog.action === "deactivate"
                    ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                    : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                }`}
              >
                {confirmDialog.action === "deactivate" ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-content-primary mb-4">Add User</h3>
            <p className="text-sm text-content-secondary mb-4">
              Pre-provision a user before their first login. They will be automatically linked to their identity when
              they sign in.
            </p>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label htmlFor="add-user-email" className="block text-sm font-medium text-content-secondary mb-1">
                  Email
                </label>
                <input
                  id="add-user-email"
                  type="email"
                  required
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="user@company.com"
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              <div>
                <label htmlFor="add-user-name" className="block text-sm font-medium text-content-secondary mb-1">
                  Name
                </label>
                <input
                  id="add-user-name"
                  type="text"
                  required
                  value={addUserForm.name}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              <div>
                <label htmlFor="add-user-role" className="block text-sm font-medium text-content-secondary mb-1">
                  Role
                </label>
                <select
                  id="add-user-role"
                  value={addUserForm.role}
                  onChange={(e) => setAddUserForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                >
                  <option value="member">Member</option>
                  <option value="platform_admin">Platform Admin</option>
                  {isInstallAdmin && <option value="install_admin">Install Admin</option>}
                </select>
              </div>
              {availableTeams.length > 0 && (
                <div>
                  <span className="block text-sm font-medium text-content-secondary mb-2">
                    Teams <span className="text-content-quaternary font-normal">(in addition to Everyone)</span>
                  </span>
                  <div className="max-h-40 overflow-y-auto space-y-1 border border-border-secondary rounded-lg p-2 bg-surface-secondary">
                    {availableTeams.map((team) => (
                      <label
                        key={team.uuid}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-interactive-hover cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={addUserTeams.includes(team.uuid)}
                          onChange={(e) => handleTeamCheckboxChange(team.uuid, e.target.checked)}
                          className="rounded border-border-secondary text-brand-primary focus:ring-brand-primary/50"
                        />
                        <span className="text-sm text-content-primary">{team.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUser(false);
                    setAddUserForm({ email: "", name: "", role: "member" });
                    setAddUserTeams([]);
                  }}
                  className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addUserSubmitting || !addUserForm.email || !addUserForm.name}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-brand-primary text-white hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addUserSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
